/**
 * `/readiness` — the free agent-readiness check (moat plan §4.5, lead magnet).
 * Landing-styled (`.mk`, light-only, no shadcn in here); the score itself comes from
 * `POST /api/public/readiness`, which runs the shared scorer in `src/lib/readiness.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check, Copy, Loader2, X } from 'lucide-react'
import { Container, MkFooter, MkLink, MkNav, Overline, SectionHead } from '#/components/landing/mk'
import { api, ApiError } from '#/lib/api'
import { cn } from '#/lib/utils'
import type { ReadinessCheckId, ReadinessGrade, ReadinessResult } from '#/lib/readiness'
import { m } from '#/paraglide/messages'

interface Report {
  repo: string
  branch: string
  result: ReadinessResult
  fetchedAt: string
}

export const Route = createFileRoute('/readiness')({
  validateSearch: (s: Record<string, unknown>): { url?: string } => ({ url: typeof s.url === 'string' && s.url ? s.url : undefined }),
  head: () => ({
    meta: [
      { title: m.rd_meta_title() },
      { name: 'description', content: m.rd_meta_desc() },
      { property: 'og:title', content: m.rd_meta_title() },
      { property: 'og:description', content: m.rd_meta_desc() },
    ],
  }),
  component: Page,
})

const LABEL: Record<ReadinessCheckId, () => string> = {
  agent_instructions: () => m.rd_check_agent_instructions(),
  skills: () => m.rd_check_skills(),
  readme: () => m.rd_check_readme(),
  tests: () => m.rd_check_tests(),
  lint: () => m.rd_check_lint(),
  ci: () => m.rd_check_ci(),
  lockfile: () => m.rd_check_lockfile(),
  env_committed: () => m.rd_check_env_committed(),
  no_secrets: () => m.rd_check_no_secrets(),
  gitignore: () => m.rd_check_gitignore(),
  docs: () => m.rd_check_docs(),
}
const FIX: Record<ReadinessCheckId, () => string> = {
  agent_instructions: () => m.rd_fix_agent_instructions(),
  skills: () => m.rd_fix_skills(),
  readme: () => m.rd_fix_readme(),
  tests: () => m.rd_fix_tests(),
  lint: () => m.rd_fix_lint(),
  ci: () => m.rd_fix_ci(),
  lockfile: () => m.rd_fix_lockfile(),
  env_committed: () => m.rd_fix_env_committed(),
  no_secrets: () => m.rd_fix_no_secrets(),
  gitignore: () => m.rd_fix_gitignore(),
  docs: () => m.rd_fix_docs(),
}
const SUMMARY: Record<ReadinessGrade, () => string> = {
  A: () => m.rd_summary_a(),
  B: () => m.rd_summary_b(),
  C: () => m.rd_summary_c(),
  D: () => m.rd_summary_d(),
  F: () => m.rd_summary_f(),
}
const TONE: Record<ReadinessGrade, string> = {
  A: 'text-emerald-600',
  B: 'text-emerald-600',
  C: 'text-amber-600',
  D: 'text-orange-600',
  F: 'text-red-600',
}

const errText = (e: unknown) => {
  const code = e instanceof ApiError ? e.code : undefined
  if (code === 'invalid_url' || code === 'bad_request') return m.rd_err_invalid_url()
  if (code === 'not_found' || code === 'empty_repo') return m.rd_err_not_found()
  if (code === 'rate_limited') return m.rd_err_rate_limited()
  return m.rd_err_generic()
}

function Page() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/readiness' })
  const [url, setUrl] = useState(search.url ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [report, setReport] = useState<Report | null>(null)
  const [copied, setCopied] = useState(false)
  const ranFor = useRef<string | null>(null)

  const run = useCallback(async (target: string) => {
    setLoading(true)
    setError(null)
    try {
      setReport(await api<Report>('/api/public/readiness', { method: 'POST', json: { url: target } }))
    } catch (e) {
      setReport(null)
      setError(errText(e))
    } finally {
      setLoading(false)
    }
  }, [])

  // shareable links: `/readiness?url=…` scores the repo as soon as the page opens
  useEffect(() => {
    const u = search.url
    if (!u || ranFor.current === u) return
    ranFor.current = u
    setUrl(u)
    void run(u)
  }, [search.url, run])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const target = url.trim()
    if (!target || loading) return
    ranFor.current = target
    void run(target)
    void navigate({ search: { url: target }, replace: true })
  }

  const share = async () => {
    const link = `${window.location.origin}/readiness?url=${encodeURIComponent(url.trim())}`
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt(m.rd_share(), link)
    }
  }

  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <section className="pt-16 pb-10 lg:pt-24">
          <Container className="flex flex-col items-center gap-6 text-center">
            <Overline>{m.rd_hero_overline()}</Overline>
            <h1 className="max-w-[18ch]">{m.rd_hero_title()}</h1>
            <p className="mk-lead max-w-[54ch]">{m.rd_hero_sub()}</p>
            <form onSubmit={submit} className="mt-2 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
              <label className="sr-only" htmlFor="rd-url">
                {m.rd_input_label()}
              </label>
              <input
                id="rd-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={m.rd_input_placeholder()}
                autoComplete="off"
                spellCheck={false}
                className="h-12 flex-1 rounded-lg border border-[var(--mk-border)] bg-white px-4 text-[15px] text-[var(--mk-fg)] outline-none placeholder:text-[var(--mk-fg-muted)] focus:border-[var(--mk-fg)]"
              />
              <button type="submit" className="mk-btn mk-btn-primary h-12 shrink-0" disabled={loading}>
                {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                {loading ? m.rd_btn_checking() : m.rd_btn_check()}
              </button>
            </form>
            {error ? <p className="text-[15px] text-red-600">{error}</p> : null}
          </Container>
        </section>

        {report ? (
          <section className="pb-16">
            <Container className="flex flex-col gap-6">
              <ScoreCard report={report} onShare={share} copied={copied} />
              <SecretsCard report={report} />
              <ChecksCard report={report} />
            </Container>
          </section>
        ) : null}

        <section className="py-16 lg:py-20">
          <Container className="flex flex-col gap-12">
            <SectionHead
              title={m.rd_cta_title()}
              lead={m.rd_cta_desc()}
              actions={
                <MkLink to="/plan" search={{ flow: 'existing' }} arrow>
                  {m.rd_cta_btn()}
                </MkLink>
              }
            />
            <div className="mk-card mx-auto flex w-full max-w-2xl flex-col gap-3 p-7">
              <h3>{m.rd_cli_title()}</h3>
              <p className="mk-muted leading-relaxed">{m.rd_cli_desc()}</p>
              <code className="mt-1 block overflow-x-auto rounded-lg border border-[var(--mk-border)] bg-white px-4 py-3 font-mono text-[13px] text-[var(--mk-fg)]">npx @bystep/cli doctor --repo</code>
            </div>
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}

function ScoreCard({ report, onShare, copied }: { report: Report; onShare: () => void; copied: boolean }) {
  const { score, grade, checks } = report.result
  const ok = checks.filter((c) => c.ok).length
  const R = 68
  const C = 2 * Math.PI * R
  return (
    <div className="mk-card-white flex flex-col items-center gap-7 p-7 sm:flex-row sm:items-center sm:gap-10">
      <div className="relative shrink-0">
        <svg width="168" height="168" viewBox="0 0 168 168" role="img" aria-label={`${score} / 100`}>
          <circle cx="84" cy="84" r={R} fill="none" stroke="var(--mk-border)" strokeWidth="12" />
          <circle
            cx="84"
            cy="84"
            r={R}
            fill="none"
            stroke="currentColor"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={`${(C * score) / 100} ${C}`}
            transform="rotate(-90 84 84)"
            className={TONE[grade]}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-5xl font-semibold tabular-nums text-[var(--mk-fg)]">{score}</span>
          <span className="text-xs text-[var(--mk-fg-muted)]">{m.rd_score_out_of()}</span>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 text-center sm:text-left">
        <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
          <span className={cn('text-2xl font-semibold', TONE[grade])}>{m.rd_grade({ grade })}</span>
          <span className="mk-muted truncate text-[15px]">{report.repo}</span>
          <span className="mk-muted text-[13px]">{m.rd_branch({ branch: report.branch })}</span>
        </div>
        <p className="text-[17px] text-[var(--mk-fg)]">{SUMMARY[grade]()}</p>
        <p className="mk-muted text-[15px]">{m.rd_passed({ ok: String(ok), total: String(checks.length) })}</p>
        <div className="mt-2 flex flex-wrap justify-center gap-3 sm:justify-start">
          <button type="button" onClick={onShare} className="mk-btn mk-btn-secondary h-10">
            {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
            {copied ? m.rd_share_done() : m.rd_share()}
          </button>
          <MkLink to="/plan" search={{ flow: 'existing' }} className="h-10" arrow>
            {m.rd_cta_btn()}
          </MkLink>
        </div>
      </div>
    </div>
  )
}

function SecretsCard({ report }: { report: Report }) {
  const { count, samples } = report.result.secrets
  if (!count)
    return (
      <p className="flex items-center gap-2 text-[15px] text-emerald-700">
        <Check className="size-4 shrink-0" aria-hidden />
        {m.rd_secrets_clean()}
      </p>
    )
  return (
    <div className="rounded-[var(--mk-radius)] border border-red-200 bg-red-50 p-6">
      <h3 className="text-red-700">{m.rd_secrets_title()}</h3>
      <p className="mt-2 text-[15px] text-red-700">{m.rd_secrets_desc({ count: String(count) })}</p>
      <ul className="mt-3 flex flex-col gap-1 font-mono text-[13px] text-red-800">
        {samples.map((s) => (
          <li key={s} className="truncate">
            {s}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ChecksCard({ report }: { report: Report }) {
  return (
    <div className="mk-card-white flex flex-col p-2">
      <h3 className="px-5 pt-4 pb-2">{m.rd_checks_title()}</h3>
      <ul className="flex flex-col">
        {report.result.checks.map((c) => (
          <li key={c.id} className="flex items-start gap-4 border-t border-[var(--mk-border)] px-5 py-4">
            <span
              aria-label={c.ok ? m.rd_pass() : m.rd_fail()}
              className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full', c.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')}
            >
              {c.ok ? <Check className="size-4" aria-hidden /> : <X className="size-4" aria-hidden />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-[15px] font-medium text-[var(--mk-fg)]">{LABEL[c.id]()}</span>
                <span className="mk-muted text-[13px] tabular-nums">{c.weight} pts</span>
                {c.detail ? <span className="mk-muted truncate font-mono text-[12px]">{c.detail}</span> : null}
              </div>
              {!c.ok ? <p className="mk-muted mt-1 text-[14px] leading-relaxed">{FIX[c.id]()}</p> : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
